<template>
  <modal
    :name="modalName"
    class="icp-modal"
    :adaptive="true"
    :scrollable="true"
    height="auto"
    width="90%"
    :maxWidth="680"
    @before-close="onBeforeClose"
  >
    <div class="icp-dialog">
      <div class="icp-dialog-header">
        <div class="icp-dialog-header-left">
          <span class="icp-dialog-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="21 15 16 10 5 21"/>
            </svg>
          </span>
          <div>
            <h2 class="icp-dialog-title">{{ title }}</h2>
            <p class="icp-dialog-sub">{{ subtitle }}</p>
          </div>
        </div>
        <button class="icp-close-btn" @click="closeModal" title="Close">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      <div class="icp-dialog-body">
        <div class="icp-crop-area">
          <VueCropper
            v-if="sourceUrl"
            ref="cropper"
            :src="sourceUrl"
            :aspect-ratio="aspectRatio"
            :view-mode="1"
            :auto-crop-area="0.85"
            :background="false"
            :responsive="true"
            :rotatable="false"
            :scalable="false"
            :zoomable="true"
            alt="Source image"
            class="icp-cropper"
          ></VueCropper>
        </div>
        <p v-if="error" class="icp-error">{{ error }}</p>
      </div>

      <div class="icp-dialog-footer">
        <button class="icp-cancel-btn" @click="closeModal">Cancel</button>
        <button class="icp-save-btn" @click="confirmCrop">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          Use Selection
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
import VueCropper from 'vue-cropperjs'
import 'cropperjs/dist/cropper.css'

export default {
  name: 'image-crop-preview-modal',
  components: { VueCropper },
  props: {
    show: { type: Boolean, default: false },
    modalName: { type: String, default: 'imageCropPreviewModal' },
    sourceUrl: { type: String, default: '' },
    fileName: { type: String, default: 'cropped-image.jpg' },
    title: { type: String, default: 'Preview Image' },
    subtitle: { type: String, default: 'Crop the image area before upload' },
    aspectRatio: { type: Number, default: 1 },
    outputWidth: { type: Number, default: 600 },
    outputHeight: { type: Number, default: 600 },
    outputType: { type: String, default: 'image/jpeg' }
  },
  data () {
    return {
      error: null
    }
  },
  watch: {
    show (value) {
      if (value) {
        this.openModal()
      } else {
        this.$modal.hide(this.modalName)
      }
    },
    sourceUrl (value) {
      this.error = null
      if (value && this.$refs.cropper) {
        this.$nextTick(() => {
          this.$refs.cropper.replace(value)
        })
      }
    }
  },
  mounted () {
    if (this.show) {
      this.openModal()
    }
  },
  methods: {
    openModal () {
      this.error = null
      this.$nextTick(() => {
        this.$modal.show(this.modalName)
      })
    },
    closeModal () {
      this.$modal.hide(this.modalName)
    },
    onBeforeClose () {
      this.$emit('closed')
    },
    confirmCrop () {
      if (!this.$refs.cropper) {
        this.error = 'Image preview is not ready yet.'
        return
      }

      var canvas = this.$refs.cropper.getCroppedCanvas({
        width: this.outputWidth,
        height: this.outputHeight,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high'
      })

      if (!canvas) {
        this.error = 'Select an image area before continuing.'
        return
      }

      var previewUrl = canvas.toDataURL(this.outputType, 0.92)
      var self = this
      canvas.toBlob(function (blob) {
        if (!blob) {
          self.error = 'Could not prepare cropped image.'
          return
        }
        self.$emit('cropped', {
          blob: blob,
          previewUrl: previewUrl,
          name: self.buildOutputFileName()
        })
        self.closeModal()
      }, this.outputType, 0.92)
    },
    buildOutputFileName () {
      var baseName = this.fileName || 'cropped-image.jpg'
      var ext = this.outputType === 'image/png' ? '.png' : '.jpg'
      var cleanBase = baseName.replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9-_]/g, '-')
      if (!cleanBase) {
        cleanBase = 'cropped-image'
      }
      return cleanBase + ext
    }
  }
}
</script>

<style>
.icp-dialog {
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
}
.icp-dialog-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 1rem 1.75rem;
  border-bottom: 1px solid #f0f0f5;
}
.icp-dialog-header-left {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.icp-dialog-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f0effe;
  color: #5b4fcf;
  flex-shrink: 0;
}
.icp-dialog-title {
  font-size: 16px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 3px 0;
}
.icp-dialog-sub {
  font-size: 12px;
  color: #8a8a9a;
  margin: 0;
}
.icp-close-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid #e8e8ed;
  background: #fff;
  color: #8a8a9a;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
}
.icp-close-btn:hover {
  border-color: #fbb;
  background: #fff5f5;
  color: #c0392b;
}
.icp-dialog-body {
  padding: 1.5rem 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.icp-crop-area {
  width: 100%;
  min-height: 360px;
  background: #fafafa;
  border: 1px solid #e8e8ed;
  border-radius: 10px;
  overflow: hidden;
}
.icp-cropper {
  width: 100%;
  height: 420px;
}
.icp-error {
  font-size: 12px;
  color: #c0392b;
  background: #fff5f5;
  border: 1px solid #fdd;
  border-radius: 7px;
  padding: 7px 12px;
  margin: 0;
}
.icp-dialog-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  border-top: 1px solid #f0f0f5;
  padding: 1rem 1.75rem 1.5rem;
}
.icp-cancel-btn,
.icp-save-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
}
.icp-cancel-btn {
  border: 1.5px solid #e0e0ea;
  background: #fff;
  color: #5b4fcf;
  padding: 0 16px;
}
.icp-cancel-btn:hover {
  border-color: #5b4fcf;
  background: #f5f3ff;
}
.icp-save-btn {
  border: none;
  background: #5b4fcf;
  color: #fff;
  padding: 0 18px;
}
.icp-save-btn:hover {
  background: #4338ca;
}

@media (max-width: 640px) {
  .icp-dialog-footer {
    align-items: stretch;
    flex-direction: column-reverse;
  }
  .icp-cancel-btn,
  .icp-save-btn {
    width: 100%;
  }
  .icp-crop-area {
    min-height: 280px;
  }
  .icp-cropper {
    height: 320px;
  }
}
</style>



// WEBPACK FOOTER //
// ImageCropPreviewModal.vue?5c54a9b4